import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

import { Button } from "shared/ui/shadcn/button";

import { LookalikeSyncStatus } from "../utils/lookalike-meeting-sync";
import { MeetingNotesField } from "./meeting-notes-field";

interface AutoSyncStepPropsI {
  status: LookalikeSyncStatus;
  meetingTime: string | null;
  isReschedule?: boolean;
  lookalikeEmail?: string;
  // GLE-8421: the SDR types notes while the invite syncs. Done stays disabled
  // until there are some; "timeout" hands them to FallbackStep.
  notes: string;
  onNotesChange: (value: string) => void;
  isSavingNotes: boolean;
  onDone: () => void;
  onEnterManually: () => void;
}

const formatMeetingTime = (iso: string | null): string | null => {
  if (!iso) {
    return null;
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleString([], { dateStyle: "medium", timeStyle: "short" });
};

/**
 * Lookalike-campaign booking result. The AE has no calendar connection, so the
 * meeting is synced from the ICS invite the lookalike guest receives. Instead
 * of optimistically claiming success, this step polls for the actual synced
 * meeting and tells the truth: detecting / found / timeout. Manual entry stays
 * available at all times.
 */
export const AutoSyncStep = ({
  status,
  meetingTime,
  isReschedule,
  lookalikeEmail,
  notes,
  onNotesChange,
  isSavingNotes,
  onDone,
  onEnterManually,
}: AutoSyncStepPropsI) => {
  const prettyTime = formatMeetingTime(meetingTime);

  return (
    <div data-testid="auto-sync-step" className="space-y-5">
      {status === "detecting" && (
        <div
          data-testid="auto-sync-detecting"
          className="rounded-lg border border-base-300 bg-base-200 p-4"
        >
          <div className="flex items-start gap-3">
            <ArrowPathIcon className="mt-0.5 h-5 w-5 flex-shrink-0 animate-spin text-base-content/60" />

            <div className="flex-1 space-y-1">
              <h4 className="typography-body-4-semibold">
                {isReschedule
                  ? "Confirming the reschedule…"
                  : "Confirming the booking…"}
              </h4>
              <p className="text-base-content/70 typography-body-5">
                We&apos;re waiting for the calendar invite to sync (up to 5
                minutes). Keep this open — it&apos;ll confirm automatically once
                the meeting comes through.
              </p>
            </div>
          </div>
        </div>
      )}

      {status === "found" && (
        <div
          data-testid="auto-sync-found"
          className="rounded-lg border border-green-200 bg-green-50 p-4"
        >
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600" />

            <div className="flex-1 space-y-1">
              <h4 className="text-green-800 typography-body-4-semibold">
                {isReschedule ? "Reschedule confirmed" : "Meeting confirmed"}
              </h4>
              <p className="text-green-700 typography-body-5">
                {prettyTime
                  ? `Synced from the calendar invite for ${prettyTime}.`
                  : "Synced from the calendar invite."}
              </p>
            </div>
          </div>
        </div>
      )}

      {status === "timeout" && (
        <div
          data-testid="auto-sync-timeout"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4"
        >
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />

            <div className="flex-1 space-y-1">
              <h4 className="text-amber-800 typography-body-4-semibold">
                We couldn&apos;t detect the meeting yet
              </h4>
              <p className="text-amber-700 typography-body-5">
                {lookalikeEmail
                  ? `The invite hasn't synced. Make sure ${lookalikeEmail} was added as a guest on the booking — or enter the details manually below.`
                  : "The invite hasn't synced. Make sure the Glencoco guest was added on the booking — or enter the details manually below."}
              </p>
            </div>
          </div>
        </div>
      )}

      {(status === "detecting" || status === "found") && (
        <MeetingNotesField
          value={notes}
          disabled={isSavingNotes}
          onChange={onNotesChange}
        />
      )}

      {status === "found" && (
        <Button
          data-testid="auto-sync-done-button"
          size="lg"
          className="w-full"
          isLoading={isSavingNotes}
          disabled={isSavingNotes || !notes.trim()}
          onClick={onDone}
        >
          {isSavingNotes ? "Saving notes…" : "Done"}
        </Button>
      )}

      <Button
        data-testid="auto-sync-manual-link"
        size={status === "timeout" ? "lg" : "default"}
        variant={status === "timeout" ? "default" : "ghost"}
        className={
          status === "timeout"
            ? "w-full"
            : "mx-auto w-full text-base-content/50 hover:text-base-content/70"
        }
        onClick={onEnterManually}
      >
        Enter meeting details manually
      </Button>
    </div>
  );
};
